import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/Common";
import { api, errMsg } from "@/lib/api";
import { RESOURCES } from "@/lib/rcc";
import { Cell, Pending, plural, pretty } from "@/components/rcc/Cells";
import { RecordForm } from "@/components/rcc/RecordForm";
import { WorkflowBuilder } from "@/components/rcc/wf/WorkflowBuilder";
import { DetailSheet } from "@/components/rcc/DetailSheet";

const sel = "h-9 rounded-md border border-[#27272A] bg-[#18181B] px-2 text-xs capitalize text-zinc-200";

// Generic list/create/detail for any RCC resource (spec §31 REST pattern).
export const ResourceTable = ({ name, title }) => {
  const R = RESOURCES[name];
  const [rows, setRows] = useState(null);
  const [missing, setMissing] = useState(false);
  const [q, setQ] = useState("");
  const [flt, setFlt] = useState({});
  const [form, setForm] = useState(null);
  const [open, setOpen] = useState(null);

  const load = () => {
    const params = Object.fromEntries(Object.entries({ q, ...flt }).filter(([, v]) => v));
    api.get(R.endpoint, { params }).then((r) => { setRows(r.data?.items || (Array.isArray(r.data) ? r.data : [])); setMissing(false); })
      .catch((e) => { if (e.response?.status === 404) setMissing(true); else toast.error(errMsg(e)); setRows([]); });
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, flt, name]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const f = (e) => setForm(e.detail ? { template: e.detail } : {}); window.addEventListener(`rcc-new-${name}`, f); return () => window.removeEventListener(`rcc-new-${name}`, f); }, [name]);

  const save = async (body) => {
    try {
      const { data } = form?.id ? await api.patch(`${R.endpoint}/${form.id}`, body) : await api.post(R.endpoint, body);
      toast.success(`${pretty(R.noun)} saved`); setForm(null); load(); if (open && data?.id === open.id) setOpen(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="space-y-3" data-testid={`rcc-res-${name}`}>
      <div className="flex flex-wrap items-center gap-2">
        {title && <h3 className="mr-auto font-display text-lg text-zinc-100">{title}</h3>}
        {R.searchable && <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-500" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${plural(R.noun)}`} className="h-9 w-56 pl-8" data-testid={`rcc-search-${name}`} /></div>}
        {(R.filters || []).map((f) => f.text
          ? <Input key={f.k} value={flt[f.k] || ""} onChange={(e) => setFlt((s) => ({ ...s, [f.k]: e.target.value }))} placeholder={f.label} className="h-9 w-36" data-testid={`rcc-filter-${name}-${f.k}`} />
          : <select key={f.k} value={flt[f.k] || ""} onChange={(e) => setFlt((s) => ({ ...s, [f.k]: e.target.value }))} className={sel} data-testid={`rcc-filter-${name}-${f.k}`}><option value="">{f.label}: all</option>{f.options.map((o) => <option key={o} value={o}>{pretty(o)}</option>)}</select>)}
        {R.fields && !R.readOnly && <Button size="sm" onClick={() => setForm({})} className="bg-[#8224e3] text-white hover:bg-[#9440f0]" data-testid={`rcc-new-${name}`}><Plus className="h-4 w-4" />New {R.noun}</Button>}
      </div>
      {missing && <Pending what={`Needs GET ${R.endpoint}.`} />}
      <div className="overflow-x-auto rounded-xl border border-[#27272A]">
        <table className="w-full min-w-[720px] text-left">
          <thead className="bg-[#121214] text-[10px] uppercase tracking-wider text-zinc-500"><tr>{R.columns.map(([k, l]) => <th key={k} className="px-3 py-2 font-medium">{l}</th>)}</tr></thead>
          <tbody>
            {!rows ? <tr><td colSpan={R.columns.length} className="p-6"><Spinner className="mx-auto h-5 w-5 text-[#8224e3]" /></td></tr>
              : !rows.length ? (missing ? null : <tr><td colSpan={R.columns.length} className="p-6 text-center text-sm text-zinc-500" data-testid={`rcc-empty-${name}`}>No {plural(R.noun)} yet.</td></tr>)
              : rows.map((r, i) => (
                <tr key={r.id || i} onClick={() => setOpen(r)} className="cursor-pointer border-t border-[#1f1f23] transition-colors hover:bg-[#8224e3]/5" data-testid={`rcc-row-${name}-${r.id || i}`}>
                  {R.columns.map(([k, , kind]) => <td key={k} className="px-3 py-2.5 align-top"><Cell v={r[k]} kind={kind} /></td>)}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {form && (name === "workflows" ? <WorkflowBuilder fields={R.fields} initial={form} onSubmit={save} onClose={() => setForm(null)} /> : <RecordForm fields={R.fields} initial={form} noun={R.noun} onSubmit={save} onClose={() => setForm(null)} />)}
      {open && <DetailSheet R={R} name={name} row={open} onClose={() => setOpen(null)} onEdit={R.fields && !R.readOnly ? () => setForm(open) : null} onChanged={(d) => { if (d) setOpen(d); load(); }} />}
    </section>
  );
};
